import {
  Box,
  BoxProps,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { HeroTexture } from "./HeroTexture";

const MotionVStack = motion(VStack);

export interface PageHeroAction {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: any;
  onClick: () => void;
  variant?: "primary" | "secondary" | "solid" | "outline" | "ghost";
}

export interface PageHeroProps {
  /** Main heading text */
  title: string;
  /** Optional highlighted/gradient word or phrase appended on a new line */
  titleHighlight?: string;
  /** Subtitle shown beneath the heading */
  subtitle?: string;
  /** Primary CTA button */
  primaryAction?: PageHeroAction;
  /** Secondary CTA button */
  secondaryAction?: PageHeroAction;
  /** Hero height — accepts any CSS/Chakra size value. Defaults to 600px */
  height?: BoxProps["minH"];
}

export const PageHero = ({
  title,
  titleHighlight,
  subtitle,
  primaryAction,
  secondaryAction,
  height = "450px",
}: PageHeroProps) => {
  const bgColor = useColorModeValue("white", "dark.900");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.600", "gray.300");
  const gradientText = useColorModeValue(
    "linear(to-r, brand.500, brand.600)",
    "linear(to-r, brand.400, brand.500)",
  );
  const bgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249,168,166,0.45), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249,168,166,0.45), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173,11,6,0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173,11,6,0.22), transparent 50%)",
  );

  return (
    <Box
      position="relative"
      display="flex"
      alignItems="center"
      bg={bgColor}
      overflow="hidden"
      minH={height}
      pt="140px"
      pb="70px"
      _before={{
        content: '""',
        position: "absolute",
        inset: 0,
        background: bgGradient,
        zIndex: 0,
      }}
    >
      <HeroTexture />

      <Container maxW="container.xl" position="relative" zIndex={2}>
        <MotionVStack
          spacing={5}
          align="center"
          textAlign="center"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="extrabold"
            color={textColor}
            lineHeight="short"
          >
            {title}
            {titleHighlight && (
              <Text
                as="span"
                display="block"
                bgGradient={gradientText}
                bgClip="text"
              >
                {titleHighlight}
              </Text>
            )}
          </Heading>

          {subtitle && (
            <Text
              fontSize={{ base: "md", md: "lg" }}
              color={subtitleColor}
              maxW="600px"
              lineHeight="tall"
            >
              {subtitle}
            </Text>
          )}

          {(primaryAction || secondaryAction) && (
            <HStack pt={2} spacing={3} flexWrap="wrap" justify="center">
              {primaryAction && (
                <Button
                  size={{ base: "md" }}
                  colorScheme="brand"
                  variant={primaryAction.variant ?? "primary"}
                  rightIcon={
                    primaryAction.icon ? (
                      <Icon verticalAlign="middle" as={primaryAction.icon} />
                    ) : undefined
                  }
                  onClick={primaryAction.onClick}
                  shadow="lg"
                  _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                  transition="all 0.3s"
                >
                  {primaryAction.label}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  size={{ base: "md" }}
                  colorScheme="brand"
                  variant={secondaryAction.variant ?? "secondary"}
                  rightIcon={
                    secondaryAction.icon ? (
                      <Icon verticalAlign="middle" as={secondaryAction.icon} />
                    ) : undefined
                  }
                  onClick={secondaryAction.onClick}
                  _hover={{ transform: "translateY(-2px)" }}
                  transition="all 0.3s"
                >
                  {secondaryAction.label}
                </Button>
              )}
            </HStack>
          )}
        </MotionVStack>
      </Container>
    </Box>
  );
};
